// ═══════════════════════════════════════════════════════════════════════════
// AI Support Assistant (CC-2d — sub-ai-support, ТЗ §13)
// ═══════════════════════════════════════════════════════════════════════════
//
// Read-only support channel: «живет внутри редактора; знает справку по
// программе; знает историю действий пользователя; знает recent command
// history; знает current tool и selection metadata; не имеет права менять
// чертеж» (ТЗ §13 AI Support Assistant).
//
// The provider SEAM (`AiSupportProvider`) is typed so an implementation can
// only ever turn a question + a READ-ONLY context snapshot into localized
// answer text — there is no dispatch sink to reach, so the support channel
// is non-mutating BY CONSTRUCTION, not by convention. A future LLM-backed
// implementation plugs in behind the same interface.
//
// The default `localAiSupportProvider` is deterministic: it answers from
// the command registry (`ALL_COMMAND_DESCRIPTORS` — the canonical RU+EN
// program help: aliases, short/long help, CLI examples) plus a small set
// of curated topics (layers, selection metadata, recent command history).
// All framing strings come from `messages.ts` (RU+EN, compile-time parity).

import {
  ALL_COMMAND_DESCRIPTORS,
  type CommandDescriptor,
} from "@dxf-ai-editor/command-registry";

import { t, type LanguageCode } from "../i18n";

/**
 * Read-only session snapshot the assistant may consult (ТЗ §13: history,
 * recent commands, current tool, selection metadata). Plain data — the
 * caller (the support panel) projects it from the store; the provider
 * never touches the store itself.
 */
export interface AiSupportContext {
  /** Latest-first command types from the visible audit ring (non-`ui/*`). */
  recentCommands: readonly string[];
  /** Last command-line submission ("current tool" signal), if any. */
  lastSubmittedCommand: string | null;
  /** Number of currently selected entities. */
  selectionCount: number;
  /** Distinct entity types in the current selection. */
  selectionTypes: readonly string[];
  /** Active space id (model / layout). */
  activeSpaceId: string;
}

/** A resolved, localized answer. */
export interface AiSupportAnswer {
  /** Ordered localized paragraphs forming the answer body. */
  paragraphs: string[];
  /** Matched topic / command ids (telemetry + tests); empty on fallback. */
  matchedTopics: string[];
  /** True when nothing matched and the fallback guidance was returned. */
  isFallback: boolean;
}

/**
 * The provider seam. Implementations receive the question + read-only
 * context and return localized text — nothing else. No dispatch sink is
 * available, so a support provider cannot mutate the document.
 */
export interface AiSupportProvider {
  answer(
    query: string,
    context: AiSupportContext,
    language: LanguageCode,
  ): AiSupportAnswer;
}

// ─── query tokenization ─────────────────────────────────────────────
//
// Deterministic keyword matching: uppercase word tokens, with common
// EN/RU function words removed so an article never collides with a
// one-letter command alias ("a line" must match LINE, not ARC via "A").

const STOPWORDS = new Set<string>([
  // EN
  "A", "AN", "THE", "I", "DO", "DOES", "HOW", "TO", "AND", "OR", "OF",
  "IS", "IT", "ITS", "IN", "ON", "MY", "ME", "CAN", "WHAT", "WHERE",
  "WITH", "FOR", "SET",
  // RU
  "КАК", "Я", "И", "ИЛИ", "ЧТО", "ГДЕ", "ЭТО", "НА", "В", "С", "ПО",
  "ДЛЯ", "У", "ЕГО", "ЕЁ", "МНЕ", "МОЙ", "МОЯ", "ЛИ", "ЖЕ",
]);

function tokenize(query: string): string[] {
  const raw = query
    .toUpperCase()
    .split(/[^\p{L}\p{N}]+/u)
    .filter((token) => token.length > 0);
  // Single-character tokens only count when the WHOLE query is that token
  // (a user typing the bare alias "L" / "О"), never inside a sentence.
  if (raw.length === 1) return raw;
  return raw.filter(
    (token) => token.length > 1 && !STOPWORDS.has(token),
  );
}

// ─── curated topics (beyond per-command registry help) ──────────────

const LAYER_KEYWORDS = new Set<string>([
  "LAYER", "LAYERS",
  "СЛОЙ", "СЛОИ", "СЛОЯ", "СЛОЁВ", "СЛОЕВ", "СЛОЕМ", "СЛОЯМ", "СЛОЯХ", "СЛОЮ",
]);

const SELECTION_KEYWORDS = new Set<string>([
  "SELECT", "SELECTED", "SELECTION",
  "ВЫДЕЛЕНИЕ", "ВЫДЕЛЕНО", "ВЫДЕЛЕН", "ВЫДЕЛИТЬ", "ВЫБОР", "ВЫБРАТЬ", "ВЫБРАНО",
]);

const RECENT_KEYWORDS = new Set<string>([
  "HISTORY", "RECENT", "LAST", "COMMANDS",
  "ИСТОРИЯ", "ПОСЛЕДНИЕ", "ПОСЛЕДНЯЯ", "ПОСЛЕДНИХ", "НЕДАВНИЕ", "ЖУРНАЛ",
]);

/** Max command-registry sections folded into one answer. */
const MAX_COMMAND_MATCHES = 3;

function matchCommands(
  tokens: readonly string[],
  descriptors: readonly CommandDescriptor[],
): CommandDescriptor[] {
  const matched: CommandDescriptor[] = [];
  const seen = new Set<string>();
  for (const token of tokens) {
    for (const descriptor of descriptors) {
      if (seen.has(descriptor.id)) continue;
      const isMatch =
        descriptor.id === token ||
        descriptor.aliases.en.includes(token) ||
        descriptor.aliases.ru.includes(token);
      if (isMatch) {
        matched.push(descriptor);
        seen.add(descriptor.id);
        if (matched.length >= MAX_COMMAND_MATCHES) return matched;
      }
    }
  }
  return matched;
}

function commandParagraphs(
  descriptor: CommandDescriptor,
  language: LanguageCode,
): string[] {
  const help = descriptor.help[language];
  const aliases = [...descriptor.aliases.en, ...descriptor.aliases.ru]
    .filter((alias) => alias !== descriptor.id)
    .join(", ");
  const paragraphs = [
    t(
      "ai.support.command_intro",
      {
        name: descriptor.id,
        aliases: aliases.length > 0 ? aliases : descriptor.id,
        short: help.short,
      },
      language,
    ),
    help.long,
  ];
  if (help.examples.length > 0) {
    paragraphs.push(
      t(
        "ai.support.command_examples",
        { examples: help.examples.join(" · ") },
        language,
      ),
    );
  }
  return paragraphs;
}

// ─── local deterministic provider ───────────────────────────────────

/**
 * Build the default registry-backed provider. `descriptors` is injectable
 * for tests; production uses the canonical `ALL_COMMAND_DESCRIPTORS`.
 */
export function createLocalAiSupportProvider(
  descriptors: readonly CommandDescriptor[] = ALL_COMMAND_DESCRIPTORS,
): AiSupportProvider {
  return {
    answer(query, context, language): AiSupportAnswer {
      const tokens = tokenize(query);
      const paragraphs: string[] = [];
      const matchedTopics: string[] = [];

      // 1. Program help — command-registry sections (RU+EN authored help).
      for (const descriptor of matchCommands(tokens, descriptors)) {
        paragraphs.push(...commandParagraphs(descriptor, language));
        matchedTopics.push(descriptor.id);
      }

      // 2. Curated topic — layers (no LAYER descriptor exists; the layer
      //    workflow spans the inspector grid, CHPROP and the Layer Manager).
      if (tokens.some((token) => LAYER_KEYWORDS.has(token))) {
        paragraphs.push(t("ai.support.topic.layers", undefined, language));
        matchedTopics.push("topic:layers");
      }

      // 3. Session context — selection metadata (ТЗ §13).
      if (tokens.some((token) => SELECTION_KEYWORDS.has(token))) {
        paragraphs.push(
          context.selectionCount > 0
            ? t(
                "ai.support.context.selection",
                {
                  count: context.selectionCount,
                  types: context.selectionTypes.join(", "),
                },
                language,
              )
            : t("ai.support.context.selection_empty", undefined, language),
        );
        matchedTopics.push("topic:selection");
      }

      // 4. Session context — recent command history (ТЗ §13).
      if (tokens.some((token) => RECENT_KEYWORDS.has(token))) {
        paragraphs.push(
          context.recentCommands.length > 0
            ? t(
                "ai.support.context.recent",
                { commands: context.recentCommands.slice(0, 5).join(", ") },
                language,
              )
            : t("ai.support.context.recent_empty", undefined, language),
        );
        if (context.lastSubmittedCommand) {
          paragraphs.push(
            t(
              "ai.support.context.last_command",
              { command: context.lastSubmittedCommand },
              language,
            ),
          );
        }
        matchedTopics.push("topic:recent");
      }

      if (paragraphs.length === 0) {
        return {
          paragraphs: [t("ai.support.no_match", undefined, language)],
          matchedTopics: [],
          isFallback: true,
        };
      }
      return { paragraphs, matchedTopics, isFallback: false };
    },
  };
}

/** Shared default instance used by the support panel. */
export const localAiSupportProvider: AiSupportProvider =
  createLocalAiSupportProvider();
